import * as React from "react";
import { useAuth } from "../context/auth";
import { authManager } from "../global";
import { useDispatch } from "react-redux";
import { setBackendError } from "conference/actions";
import { Box, Button, Typography } from "@mui/material";
import { defineMessage, FormattedMessage, IntlProvider } from "react-intl";
import { useTranslation } from "../context/translation";

// Rendered before the main application tree exists, so this component cannot rely on
// the normal Translations wrapper from App.tsx.
const FullScreenMessage = ({ children }: { children: React.ReactNode }) => (
    <Box
        sx={{
            alignItems: "center",
            display: "flex",
            minHeight: "100vh",
            justifyContent: "center",
            padding: 2,
            textAlign: "center",
        }}
    >
        <Typography variant="h6" component="div" color="textSecondary">
            {children}
        </Typography>
    </Box>
);

const NoConferenceRole = () => (
    <FullScreenMessage>
        <FormattedMessage
            {...defineMessage({
                id: "auth.noConferenceRole",
                defaultMessage: "You don't have permission to use this service.",
            })}
        />
    </FullScreenMessage>
);

// The BackendErrorDialog lives below this component, so an error at start-up has to be reported here.
const RolesUnavailable = () => (
    <FullScreenMessage>
        <FormattedMessage
            {...defineMessage({
                id: "auth.rolesUnavailable",
                defaultMessage: "Cannot determine your permissions. Please try again later.",
            })}
        />
    </FullScreenMessage>
);

// Not sent to the login: when the backend cannot reach Heimdall, every new login would end up here again.
const LoginUnavailable = () => (
    <FullScreenMessage>
        <FormattedMessage
            {...defineMessage({
                id: "auth.loginUnavailable",
                defaultMessage: "The login service is not available. Please try again later.",
            })}
        />
    </FullScreenMessage>
);

const LoginFailed = ({ failure, onRetry }: { failure: "cancelled" | "failed"; onRetry: () => void }) => (
    <FullScreenMessage>
        {failure === "cancelled" ? (
            <FormattedMessage
                {...defineMessage({
                    id: "auth.loginCancelled",
                    defaultMessage: "Sign-in was cancelled.",
                })}
            />
        ) : (
            <FormattedMessage
                {...defineMessage({
                    id: "auth.loginFailed",
                    defaultMessage: "Sign-in failed. Please try again.",
                })}
            />
        )}
        <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={onRetry}>
                <FormattedMessage
                    {...defineMessage({
                        id: "auth.retryLogin",
                        defaultMessage: "Sign in again",
                    })}
                />
            </Button>
        </Box>
    </FullScreenMessage>
);

// EnsureAuthenticatedUser must stay outside Translations so auth can fetch the
// server-provided user profile before Translation.locale picks the locale.
// This no-role branch still needs localized text, so it creates a narrow
// IntlProvider from the already-fetched TranslationContext.
const Localized = ({ children }: { children: React.ReactNode }) => {
    const translations = useTranslation();

    return (
        <IntlProvider locale={translations.locale} messages={translations.translations}>
            {children}
        </IntlProvider>
    );
};

const EnsureAuthenticatedUser = ({ children }: { children: React.ReactNode }) => {
    const auth = useAuth();
    const dispatch = useDispatch();
    const loginFailure = new URLSearchParams(window.location.search).get("login");

    const login = () => {
        authManager.login().catch((e) => {
            dispatch(setBackendError({ code: "cant_reach_heimdall", details: e.toString() }));
        });
    };

    const retryLogin = () => {
        // The callback should return to the page without carrying the previous failure into a successful login.
        const destination = new URL(window.location.href);
        destination.searchParams.delete("login");
        window.history.replaceState(
            window.history.state,
            "",
            `${destination.pathname}${destination.search}${destination.hash}`,
        );
        login();
    };

    if (auth.sessionUnavailable) {
        return (
            <Localized>
                <LoginUnavailable />
            </Localized>
        );
    } else if (!auth.isLoggedIn) {
        if (loginFailure === "cancelled" || loginFailure === "failed") {
            return (
                <Localized>
                    <LoginFailed failure={loginFailure} onRetry={retryLogin} />
                </Localized>
            );
        }

        login();
        return null;
    } else if (auth.rolesFetchFailed) {
        // Not the same as having no roles, the roles are simply unknown.
        return (
            <Localized>
                <RolesUnavailable />
            </Localized>
        );
    } else if (!auth.hasConferenceRole) {
        return (
            <Localized>
                <NoConferenceRole />
            </Localized>
        );
    }

    return <React.Fragment>{children}</React.Fragment>;
};

export default EnsureAuthenticatedUser;
